import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, Mail, Phone, Search, Filter, CheckCircle, AlertCircle, Clock, XCircle, RefreshCw, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import Layout from './Layout';
import { getCustomerCommsApi, getCommsSummaryApi } from '../services/customerCommsApi';

const PAGE_SIZE = 30;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns { dateFrom: 'YYYY-MM-DD', dateTo: 'YYYY-MM-DD' } for a given month offset from today. */
const getMonthRange = (offset = 0) => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { dateFrom, dateTo };
};

/** Format a month label from an offset, e.g. "April 2026" */
const getMonthLabel = (offset) => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return d.toLocaleString('en-AU', { month: 'long', year: 'numeric' });
};

/**
 * Converts waitingSeconds into a human-readable duration string.
 */
const formatWaitingTime = (seconds) => {
    if (seconds == null) return null;
    const mins = Math.floor(seconds / 60);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    if (hours < 24) return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

/**
 * Returns a Tailwind bg class based on how long the request has been waiting.
 * Only applies to SENT (awaiting response) rows.
 *   > 30 min  → red
 *   > 20 min  → dark orange
 *   > 10 min  → light orange
 */
const getWaitingRowClass = (status, waitingSeconds) => {
    if (status !== 'SENT' || waitingSeconds == null) return '';
    const mins = waitingSeconds / 60;
    if (mins >= 30) return 'bg-red-50';
    if (mins >= 20) return 'bg-orange-100';
    if (mins >= 10) return 'bg-orange-50';
    return '';
};

const STATUS_CONFIG = {
    SENT:      { label: 'Awaiting',  icon: Clock,         color: 'text-amber-700',   bg: 'bg-amber-100' },
    APPROVED:  { label: 'Approved',  icon: CheckCircle,   color: 'text-emerald-700', bg: 'bg-emerald-100' },
    DECLINED:  { label: 'Declined',  icon: XCircle,       color: 'text-red-700',     bg: 'bg-red-100' },
    RESPONDED: { label: 'Responded', icon: MessageSquare, color: 'text-blue-700',    bg: 'bg-blue-100' },
    EXPIRED:   { label: 'Expired',   icon: AlertCircle,   color: 'text-gray-600',    bg: 'bg-gray-100' },
};

const CommunicationsPage = () => {
    const [comms, setComms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Month offset (0 = current month, -1 = last month, etc.)
    const [monthOffset, setMonthOffset] = useState(0);

    // Summary counts from /summary endpoint (for the whole month, not just the page)
    const [summary, setSummary] = useState({ SENT: 0, APPROVED: 0, DECLINED: 0, RESPONDED: 0, EXPIRED: 0, total: 0 });

    // Client-side filters
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const monthRange = useMemo(() => getMonthRange(monthOffset), [monthOffset]);
    const monthLabel = useMemo(() => getMonthLabel(monthOffset), [monthOffset]);
    const isCurrentMonth = monthOffset === 0;

    const fetchComms = useCallback(async (pageNum, range = monthRange) => {
        setLoading(true);
        setError(null);
        try {
            const [data, summaryData] = await Promise.all([
                getCustomerCommsApi({ page: pageNum, size: PAGE_SIZE, dateFrom: range.dateFrom, dateTo: range.dateTo }),
                getCommsSummaryApi({ dateFrom: range.dateFrom, dateTo: range.dateTo }),
            ]);
            setComms(data.content || []);
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
            setPage(data.number ?? pageNum);
            setSummary(summaryData || { SENT: 0, APPROVED: 0, DECLINED: 0, RESPONDED: 0, EXPIRED: 0, total: 0 });
        } catch (err) {
            console.error('Failed to load customer comms:', err);
            setError('Failed to load communications. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [monthRange]);

    // Refetch when month changes
    useEffect(() => {
        setPage(0);
        setFilter('all');
        setSearchTerm('');
        fetchComms(0, monthRange);
    }, [monthOffset]); // eslint-disable-line react-hooks/exhaustive-deps

    // Client-side filtering
    const filteredComms = comms.filter(c => {
        if (filter === 'awaiting'  && c.status !== 'SENT') return false;
        if (filter === 'responded' && c.status !== 'RESPONDED') return false;
        if (filter === 'expired'   && c.status !== 'EXPIRED') return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                c.customerName?.toLowerCase().includes(term) ||
                c.vehicleRego?.toLowerCase().includes(term) ||
                c.jobNumber?.toLowerCase().includes(term)
            );
        }
        return true;
    });

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            fetchComms(newPage);
        }
    };

    const formatDateTime = (instant) => {
        if (!instant) return '—';
        return new Date(instant).toLocaleString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Toolbar — all controls in one card */}
                <div className="bg-white rounded-2xl border border-gray-200 mb-0">

                    {/* Row 1: Month nav + search + stat pills */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-gray-100">

                        {/* Month navigator */}
                        <div className="flex items-center gap-1 shrink-0">
                            <Calendar size={16} className="text-gray-400 mr-1" />
                            <button
                                onClick={() => setMonthOffset(prev => prev - 1)}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Previous month"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm font-semibold text-gray-700 min-w-[130px] text-center">
                                {monthLabel}
                            </span>
                            <button
                                onClick={() => setMonthOffset(prev => prev + 1)}
                                disabled={isCurrentMonth}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Next month"
                            >
                                <ChevronRight size={16} />
                            </button>
                            {!isCurrentMonth && (
                                <button
                                    onClick={() => setMonthOffset(0)}
                                    className="ml-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    Today
                                </button>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="hidden lg:block w-px h-6 bg-gray-200" />

                        {/* Search */}
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search name, rego, job #..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none w-64"
                            />
                        </div>

                        {/* Refresh */}
                        <button
                            onClick={() => fetchComms(page)}
                            disabled={loading}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                            title="Refresh"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        </button>

                        {/* Stat pills — pushed right */}
                        <div className="flex items-center gap-2 lg:ml-auto flex-wrap">
                            {[
                                { key: 'SENT',      label: 'Awaiting',  count: summary.SENT,      border: 'border-amber-200', text: 'text-amber-700', bg: 'bg-amber-50' },
                                { key: 'RESPONDED', label: 'Responded', count: summary.RESPONDED, border: 'border-blue-200',  text: 'text-blue-700',  bg: 'bg-blue-50' },
                                { key: 'EXPIRED',   label: 'Expired',   count: summary.EXPIRED,   border: 'border-gray-200',  text: 'text-gray-600',  bg: 'bg-gray-50' },
                            ].map(({ key, label, count, border, text, bg }) => (
                                <span
                                    key={key}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${border} ${text} ${
                                        count > 0 ? bg : 'bg-white opacity-60'
                                    }`}
                                >
                                    <span className="font-bold text-sm">{count}</span>
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Row 2: Status filter tabs */}
                    <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
                        <Filter size={16} className="text-gray-400" />
                        {['all', 'awaiting', 'responded', 'expired'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                                    filter === f ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {f === 'awaiting' ? 'Awaiting' : f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* Error State */}
                    {error && (
                        <div className="p-4 bg-red-50 border-b border-red-100 text-red-700 text-sm flex items-center gap-2">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                            <tr className="border-b border-gray-100">
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                                <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                                <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Waiting</th>
                                <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Sent By</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading && comms.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-12 text-center text-gray-400">
                                        <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                                        Loading communications...
                                    </td>
                                </tr>
                            ) : filteredComms.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-12 text-center text-gray-400">
                                        <MessageSquare size={24} className="mx-auto mb-2 opacity-40" />
                                        {searchTerm || filter !== 'all'
                                            ? 'No communications match your filters.'
                                            : 'No confirmation requests found.'}
                                    </td>
                                </tr>
                            ) : (
                                filteredComms.map((comm) => {
                                    const statusCfg = STATUS_CONFIG[comm.status] || STATUS_CONFIG.SENT;
                                    const StatusIcon = statusCfg.icon;
                                    const rowBg = getWaitingRowClass(comm.status, comm.waitingSeconds);

                                    return (
                                        <tr key={comm.uuid} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${rowBg}`}>
                                            {/* Customer */}
                                            <td className="p-4">
                                                <div>
                                                    <p className="font-medium text-gray-900">{comm.customerName}</p>
                                                    <p className="text-sm text-gray-500">{comm.contactValue}</p>
                                                </div>
                                            </td>

                                            {/* Vehicle */}
                                            <td className="p-4">
                                                <div>
                                                    <p className="font-medium text-gray-900">{comm.vehicleRego}</p>
                                                    <p className="text-sm text-gray-500">{comm.vehicleDescription}</p>
                                                </div>
                                            </td>

                                            {/* Job */}
                                            <td className="p-4">
                                                    <span className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                                                        {comm.jobNumber}
                                                    </span>
                                            </td>

                                            {/* Channel */}
                                            <td className="p-4 text-center">
                                                {comm.sentVia === 'EMAIL' ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                                            <Mail size={12} /> Email
                                                        </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                                            <Phone size={12} /> SMS
                                                        </span>
                                                )}
                                            </td>

                                            {/* Sent Date */}
                                            <td className="p-4 text-sm text-gray-600 whitespace-nowrap">
                                                {formatDateTime(comm.sentAt)}
                                            </td>

                                            {/* Status */}
                                            <td className="p-4 text-center">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>
                                                        <StatusIcon size={12} />
                                                        {statusCfg.label}
                                                    </span>
                                            </td>

                                            {/* Waiting */}
                                            <td className="p-4">
                                                {comm.status === 'SENT' && comm.waitingSeconds != null ? (
                                                    <span className="flex items-center gap-1 text-sm font-medium text-amber-700">
                                                            <Clock size={14} />
                                                        {formatWaitingTime(comm.waitingSeconds)}
                                                        </span>
                                                ) : comm.respondedAt ? (
                                                    <span className="text-sm text-gray-500">{formatDateTime(comm.respondedAt)}</span>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>

                                            {/* Sent By */}
                                            <td className="p-4 text-sm text-gray-600 whitespace-nowrap">
                                                {comm.sentByStaffName}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t border-gray-100">
                            <p className="text-sm text-gray-500">
                                Page {page + 1} of {totalPages} ({totalElements} total)
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 0}
                                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft size={16} /> Previous
                                </button>

                                {/* Page number buttons — show up to 5 around current page */}
                                {Array.from({ length: totalPages }, (_, i) => i)
                                    .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1)
                                    .reduce((acc, i, idx, arr) => {
                                        if (idx > 0 && i - arr[idx - 1] > 1) {
                                            acc.push('ellipsis-' + i);
                                        }
                                        acc.push(i);
                                        return acc;
                                    }, [])
                                    .map((item) => {
                                        if (typeof item === 'string') {
                                            return <span key={item} className="px-2 py-1.5 text-sm text-gray-400">…</span>;
                                        }
                                        return (
                                            <button
                                                key={item}
                                                onClick={() => handlePageChange(item)}
                                                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                                    item === page
                                                        ? 'bg-blue-600 text-white'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                            >
                                                {item + 1}
                                            </button>
                                        );
                                    })}

                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages - 1}
                                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Next <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Single page info */}
                    {totalPages <= 1 && comms.length > 0 && (
                        <div className="flex items-center justify-between p-4 border-t border-gray-100">
                            <p className="text-sm text-gray-500">Showing {filteredComms.length} of {totalElements} communications</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default CommunicationsPage;